import {
    collection,
    doc,
    addDoc,
    getDoc,
    getDocs,
    updateDoc,
    query,
    where,
    orderBy,
    limit,
    Timestamp,
    serverTimestamp
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import { DayClose } from '../../types/dayClose';
import { eventBus, EVENTS } from '../../utils/eventBus';
import { getLogicalDayId } from '../../utils/dateUtils';

const COLLECTION = 'dayCloses';

// 將 Firestore 文檔轉換為 DayClose 物件
const convertToDayClose = (doc: any): DayClose => {
    const data = doc.data();
    return {
        id: doc.id,
        userId: data.userId,
        date: data.date, // date 現在是 yyyy-MM-dd 格式的字符串
        // 新流程欄位
        dayMode: data.dayMode,
        meaningfulActivities: data.meaningfulActivities || [],
        ripples: data.ripples,
        continuationItems: data.continuationItems || [],
        // 舊流程欄位（向後相容）
        completedTimeBoxes: data.completedTimeBoxes || [],
        incompleteTimeBoxes: data.incompleteTimeBoxes || [],
        incompleteActions: data.incompleteActions || [],
        reflectionCaptureId: data.reflectionCaptureId,
        timeBoxCaptureIds: data.timeBoxCaptureIds || [],
        // Event 相關字段
        completedEvents: data.completedEvents || [],
        incompleteEvents: data.incompleteEvents || [],
        eventCaptureIds: data.eventCaptureIds || [],
        // 共用欄位
        aiSuggestions: data.aiSuggestions,
        aiReflectionFeedback: data.aiReflectionFeedback,
        status: data.status || 'partial',
        createdAt: data.createdAt,
        updatedAt: data.updatedAt
    };
};

// 開始一個新的 Day Close 流程
export const startDayClose = async (userId: string): Promise<string> => {
    try {
        // 檢查今天是否已經存在 Day Close
        const existingDayClose = await getTodayDayClose(userId);
        if (existingDayClose) {
            return existingDayClose.id;
        }

        // 創建新的 Day Close 記錄（強制使用新流程）
        const dayCloseData = {
            userId,
            date: getLogicalDayId(), // 使用新的輔助函數生成日期ID
            dayMode: 'goal_contribution', // 預設模式，用戶可在 Step 1 修改
            completedTimeBoxes: [],
            incompleteTimeBoxes: [],
            incompleteActions: [],
            status: 'partial',
            createdAt: serverTimestamp()
        };

        const docRef = await addDoc(collection(db, COLLECTION), dayCloseData);

        // 發布 Day Close 開始事件
        eventBus.publish(EVENTS.DAYCLOSE.STARTED, docRef.id, userId);

        return docRef.id;
    } catch (error) {
        console.error('Start Day Close failed:', error);
        throw new Error('Unable to start Day Close');
    }
};

// 獲取今天的 Day Close 記錄
export const getTodayDayClose = async (userId: string): Promise<DayClose | null> => {
  try {
    const logicalDateStr = getLogicalDayId();

    const q = query(
      collection(db, COLLECTION),
      where('userId', '==', userId),
      where('date', '==', logicalDateStr),
      limit(1)
    );
    
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return null;
    }
    
    const doc = querySnapshot.docs[0];
    return convertToDayClose(doc);
  } catch (error) {
    console.error('Get today Day Close failed:', error);
    // 在這種情況下，返回 null 是合適的，讓調用者處理
    return null;
  }
};

// 保存時間箱處理結果
export const saveTimeBoxProcessing = async (
    dayCloseId: string,
    completedTimeBoxes: string[],
    incompleteTimeBoxes: string[],
    incompleteActions: DayClose['incompleteActions'],
    timeBoxCaptureIds?: string[]
): Promise<void> => {
    try {
        const dayCloseRef = doc(db, COLLECTION, dayCloseId);

        const updateData: any = {
            completedTimeBoxes,
            incompleteTimeBoxes,
            incompleteActions,
            updatedAt: serverTimestamp()
        };

        // 只有當 timeBoxCaptureIds 存在時才添加到更新數據中
        if (timeBoxCaptureIds) {
            updateData.timeBoxCaptureIds = timeBoxCaptureIds;
        }

        await updateDoc(dayCloseRef, updateData);

        // 發布時間箱處理完成事件
        eventBus.publish(EVENTS.DAYCLOSE.TIMEBOXES_PROCESSED, dayCloseId);
    } catch (error) {
        console.error('Save timeBox processing failed:', error);
        throw new Error('Unable to save timeBox processing results');
    }
};

// 保存反思內容
export const saveReflection = async (
    dayCloseId: string,
    reflectionCaptureId: string
): Promise<void> => {
    try {
        const dayCloseRef = doc(db, COLLECTION, dayCloseId);

        await updateDoc(dayCloseRef, {
            reflectionCaptureId,
            updatedAt: serverTimestamp()
        });

        // 發布反思保存完成事件
        eventBus.publish(EVENTS.DAYCLOSE.REFLECTION_SAVED, dayCloseId, reflectionCaptureId);
    } catch (error) {
        console.error('Save reflection failed:', error);
        throw new Error('Unable to save reflection');
    }
};

// 保存 AI 建議
export const saveAISuggestions = async (
    dayCloseId: string,
    aiSuggestions: string[]
): Promise<void> => {
    try {
        const dayCloseRef = doc(db, COLLECTION, dayCloseId);

        await updateDoc(dayCloseRef, {
            aiSuggestions,
            updatedAt: serverTimestamp()
        });
    } catch (error) {
        console.error('Save AI suggestions failed:', error);
        throw new Error('Unable to save AI suggestions');
    }
};

/**
 * 通用更新 DayClose 記錄（支援新流程的部分更新）
 *
 * 此函數專為新的 Day Close 流程設計，允許靈活地更新任意欄位組合。
 * 舊流程應繼續使用專門函數（saveReflection、saveTimeBoxProcessing）。
 *
 * @param dayCloseId - DayClose 文檔 ID
 * @param updates - 要更新的欄位（部分更新）
 *
 * @example
 * // Step 1: 保存 Day Mode
 * await updateDayClose(dayCloseId, { dayMode: 'goal_contribution' });
 *
 * @example
 * // Step 2: 保存有意義活動和 Ripples
 * await updateDayClose(dayCloseId, {
 *   meaningfulActivities: [...],
 *   ripples: {...},
 *   aiReflectionFeedback: {...}
 * });
 */
export const updateDayClose = async (
    dayCloseId: string,
    updates: Partial<Omit<DayClose, 'id' | 'userId' | 'createdAt'>>
): Promise<void> => {
    try {
        const dayCloseRef = doc(db, COLLECTION, dayCloseId);

        await updateDoc(dayCloseRef, {
            ...updates,
            updatedAt: serverTimestamp()
        });

        // 發布更新事件（僅記錄安全資訊）
        eventBus.publish(EVENTS.DAYCLOSE.UPDATED, dayCloseId);
    } catch (error) {
        console.error('Update Day Close failed:', error);
        throw new Error('Unable to update Day Close');
    }
};

// 完成 Day Close 流程
export const completeDayClose = async (
    dayCloseId: string,
    status: 'completed' | 'partial' = 'completed'
): Promise<void> => {
    try {
        const dayCloseRef = doc(db, COLLECTION, dayCloseId);

        await updateDoc(dayCloseRef, {
            status,
            updatedAt: serverTimestamp()
        });

        // 發布 Day Close 完成事件
        eventBus.publish(EVENTS.DAYCLOSE.COMPLETED, dayCloseId, status);
    } catch (error) {
        console.error('Complete Day Close failed:', error);
        throw new Error('Unable to complete Day Close');
    }
};

// 獲取 Day Close 詳情
export const getDayCloseById = async (id: string): Promise<DayClose | null> => {
    try {
        const dayCloseRef = doc(db, COLLECTION, id);
        const docSnap = await getDoc(dayCloseRef);

        if (!docSnap.exists()) {
            return null;
        }

        return convertToDayClose(docSnap);
    } catch (error) {
        console.error('Get Day Close by ID failed:', error);
        return null;
    }
};